import { UsersTabView } from '@valiant/ui-shared'
import type {
  UserData,
  GroupOption,
  CompanyOption,
  CreateUserInput,
  CreateGroupOwnerInput,
  UserOption,
  UserRole,
} from '@valiant/ui-shared'
import {
  useGetUsersQuery,
  useGetGroupsQuery,
  useGetCompaniesQuery,
  useCreateUserMutation,
  useCreateGroupOwnerMutation,
  useAddUserToGroupMutation,
  useRemoveUserFromCompanyMutation,
  useRemoveUserFromGroupMutation,
  useAssignUserToCompanyMutation,
} from '../../generated/graphql'
import { useDebounce } from '@valiant/ui-shared'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface UsersTabProps {
  groupId?: string
  companyId?: string
  adminOnly?: boolean
  searchTerm?: string
  availableRoleFilters?: string[]
  allowedRoles?: string[]
  showUnassignUserFromGroup?: boolean
}

export function UsersTab({
  groupId,
  companyId,
  adminOnly = false,
  searchTerm,
  availableRoleFilters,
  allowedRoles,
  showUnassignUserFromGroup = false,
}: UsersTabProps = {}) {
  const navigate = useNavigate()
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all')
  const [submitLoading, setSubmitLoading] = useState(false)
  const [cursor, setCursor] = useState<{ after?: string; before?: string }>({})

  // Debounce search terms
  const debouncedSearchTerm = useDebounce(searchTerm)
  const debouncedUserSearchTerm = useDebounce(userSearchTerm)

  // Build filter for main users query
  const filter = {
    ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
    ...(groupId && { groupId }),
    ...(companyId && { companyId }),
    // ...(selectedRole !== 'all' && { role: selectedRole }),
    ...(adminOnly && { adminOnly }),
  }

  // Reset cursor when filter changes
  const filterKey = JSON.stringify(filter)
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setCursor({})
  }

  // GraphQL queries
  const [{ data: usersData, fetching: _usersLoading }, refetchUsers] = useGetUsersQuery({
    variables: {
      filter: Object.keys(filter).length > 0 ? filter : undefined,
      pagination: { first: 20, after: cursor.after, before: cursor.before },
    },
  })

  const [{ data: groupsData }] = useGetGroupsQuery()
  const [{ data: companiesData }] = useGetCompaniesQuery()

  // Query for available users (for adding existing users)
  const [{ data: availableUsersData, fetching: availableUsersLoading }] = useGetUsersQuery({
    variables: {
      filter: {
        ...(debouncedUserSearchTerm && { search: debouncedUserSearchTerm }),
        ...(groupId && !companyId && { excludeGroupId: groupId }),
      },
      pagination: {
        first: 100,
      },
    },
  })

  // Mutations
  const [, createUser] = useCreateUserMutation()
  const [, createGroupOwner] = useCreateGroupOwnerMutation()
  const [, addUserToGroup] = useAddUserToGroupMutation()
  const [, removeUserFromCompany] = useRemoveUserFromCompanyMutation()
  const [, removeUserFromGroup] = useRemoveUserFromGroupMutation()
  const [, assignUserToCompany] = useAssignUserToCompanyMutation()

  // Data mapping
  const users: UserData[] = (usersData?.getUsers.nodes || [])
    .filter((user) => {
      return availableRoleFilters ? availableRoleFilters.includes(user.role) : true
    })
    .map((user) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role as UserRole,
      isWhitelisted: user.isWhitelisted,
      isActive: user.isActive,
      lastActive: user.lastActive || '',
      companiesCount: user.companiesCount,
      groupsCount: user.groupsCount,
    }))

  const filteredUsers = users.filter((user) => {
    if (selectedRole !== 'all') {
      return user.role === selectedRole
    }
    return true
  })

  const groups: GroupOption[] = (groupsData?.getGroups || []).map((group) => ({
    groupId: group.groupId,
    name: group.name,
    status: group.status as 'ACTIVE' | 'DEACTIVATED',
  }))

  const companies: CompanyOption[] = (companiesData?.getCompanies || []).map((company) => ({
    id: company.id,
    company: company.company,
    groupId: company.groupId,
    group: company.group,
  }))

  const availableUsers: UserOption[] = (availableUsersData?.getUsers.nodes || [])
    .filter((user) => {
      if (allowedRoles) {
        if (allowedRoles.includes(user.role)) {
          return true
        } else {
          return false
        }
      }
      // For company pages, only show TRADER users
      // For group pages, show both TRADER and GROUP_OWNER roles
      // SUPER_ADMIN and COMPLIANCE are MSB-level roles assigned to all groups/companies
      if (companyId) {
        return user.role === 'TRADER'
      }
      if (groupId) {
        return user.role === 'TRADER' || user.role === 'GROUP_OWNER'
      }
    })
    .map((user) => ({
      id: user.id,
      username: user.username,
      email: user.email,
    }))

  // Callback implementations
  const handleManageUser = (user: UserData) => {
    if (user.role === 'TRADER') {
      navigate(`/users/${user.id}`)
    } else if (user.role === 'GROUP_OWNER') {
      navigate(`/group-owner/${user.id}`)
    }
  }

  const handleCreateUser = async (input: CreateUserInput) => {
    try {
      setSubmitLoading(true)
      const result = await createUser({
        input: {
          username: input.username,
          email: input.email,
          isWhitelisted: input.isWhitelisted,
          groupIds: input.groupIds,
          companyIds: input.companyIds,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(
        `User "${input.username}" created successfully and assigned to ${input.groupIds.length} group(s) and ${input.companyIds.length} company(ies)`,
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create user', { description: errorMessage })
      throw error
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleCreateGroupOwner = async (input: CreateGroupOwnerInput) => {
    try {
      setSubmitLoading(true)
      const result = await createGroupOwner({
        input: {
          username: input.username,
          email: input.email,
          groupIds: input.groupIds,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`Group Owner "${input.username}" created successfully`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to create group owner', { description: errorMessage })
      throw error
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleAddUserToGroup = async (userId: string, targetGroupId: string, asAdmin: boolean) => {
    try {
      setSubmitLoading(true)
      const result = await addUserToGroup({
        input: {
          userId,
          groupId: targetGroupId,
          asAdmin,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success(`User added ${asAdmin ? 'as group admin' : 'to group'} successfully!`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to add user ${asAdmin ? 'as group admin' : 'to group'}`, {
        description: errorMessage,
      })
      throw error
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleAddUserToCompany = async (userId: string, targetCompanyId: string) => {
    try {
      setSubmitLoading(true)
      const result = await assignUserToCompany({
        input: {
          userId,
          companyId: [targetCompanyId],
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('User added to company successfully!')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      toast.error('Failed to add user to company', { description: errorMessage })
      throw error
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleValidationError = (_field: string, _message: string) => {
    // Validation errors are handled by the component internally
    // This callback is for additional error handling if needed
  }

  const handleUnassignUserFromCompany = companyId
    ? async (userId: string, targetCompanyId: string) => {
        if (!targetCompanyId) return

        try {
          const result = await removeUserFromCompany({
            input: {
              userId,
              companyId: targetCompanyId,
            },
          })

          if (result.error) {
            throw new Error(result.error.message)
          }

          toast.success('User successfully unassigned from company!')
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'An unexpected error occurred'
          toast.error('Failed to unassign user from company', { description: errorMessage })
          throw error
        }
      }
    : undefined

  const handleUnassignUserFromGroup = showUnassignUserFromGroup
    ? async (userId: string, groupId: string) => {
        try {
          const result = await removeUserFromGroup({
            input: {
              userId,
              groupId,
            },
          })

          if (result.error) {
            throw new Error(result.error.message)
          }

          toast.success('User successfully unassigned from group!')
          refetchUsers()
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'An unexpected error occurred'
          toast.error('Failed to unassign user from group', { description: errorMessage })
          throw error
        }
      }
    : undefined

  return (
    <UsersTabView
      selectedRole={selectedRole}
      onRoleChange={(role) => setSelectedRole(role as UserRole | 'all')}
      users={filteredUsers}
      loading={submitLoading}
      title={companyId ? 'Company Users' : adminOnly ? 'Group Owners' : 'Users'}
      showCreateButton={true}
      showAddExistingButton={!!groupId}
      showViewPermissionsButton={true}
      onManageUser={handleManageUser}
      groupId={groupId}
      companyId={companyId}
      adminOnly={adminOnly}
      groups={groups}
      companies={companies}
      availableUsers={availableUsers.filter((user) => !users.some((u) => u.id === user.id))}
      usersLoading={availableUsersLoading}
      onCreateUser={handleCreateUser}
      onCreateGroupOwner={handleCreateGroupOwner}
      onCreateSuperAdmin={undefined}
      onCreateComplianceOfficer={undefined}
      onAddUserToGroup={handleAddUserToGroup}
      onAddUserToCompany={handleAddUserToCompany}
      onUserSearchChange={setUserSearchTerm}
      onValidationError={handleValidationError}
      onUnassignUserFromCompany={handleUnassignUserFromCompany}
      onUnassignUserFromGroup={handleUnassignUserFromGroup}
      availableRoleFilters={availableRoleFilters}
      allowedRoles={allowedRoles as UserRole[]}
      pageInfo={usersData?.getUsers?.pageInfo}
      totalCount={usersData?.getUsers?.totalCount}
      showPagination={true}
      onNextPage={(endCursor) => setCursor({ after: endCursor })}
      onPreviousPage={(startCursor) => setCursor({ before: startCursor })}
    />
  )
}
